// web/src/shell.ts
// 全站外壳：
//   · **站点导航条**（唯一吸顶头部）：品牌 + 横向导航 + 语言切换（+ 窄屏的本页大纲按钮）
//   · 左侧常驻的**本页大纲**列（仅当页面有足够标题时出现）
//
// 设计：
// - 页面 HTML 里直接写好 `<header class="site-navbar">`（品牌 + `.nav`），
//   本模块负责规范化与增强：统一导航项、补挂大纲按钮、实测高度写入 --nav-h、
//   按需创建左栏。若页面漏写导航条，本模块会用页面里已有的 `.brand` / `.nav`
//   现场拼一个，保证外壳始终成立。
// - 教程页的 `[data-toc-panel]` 会被**移动**进左栏。
// - 由 i18n.ts 在最前面 import 本模块，保证外壳先于各页面脚本建立。

import { buildToc, decorateHeadings, headingLabel } from "./toc";

interface NavGroup {
  href: string;
  /** 该页面族下应高亮的导航项 */
  prefix?: string;
}

/** 子页面 → 应高亮的导航项 */
const FAMILY: NavGroup[] = [
  { href: "/quickstart.html", prefix: "/quickstart/" },
  { href: "/advanced.html", prefix: "/advanced/" },
  { href: "/plugin-manuals.html", prefix: "/manuals/" },
  // 「软件界面详细说明」是产品矩阵的延伸页（导航里没有独立入口）
  { href: "/products.html", prefix: "/details/" },
  // 免费版下载页也属于产品线（导航里没有独立入口）
  { href: "/products.html", prefix: "/free/" },
];

/** 不在导航里、但属于某个导航流程的页面 → 应高亮的导航项 */
const EXACT_ACTIVE: Record<string, string> = {
  "/result.html": "/buy.html", // 付款结果页属于「购买授权」流程
  "/details.html": "/products.html", // 界面详细说明总入口属于「产品矩阵」这条线
  // 角色绑定代工服务页不占独立导航位（导航已满），归到「购买授权」这条线
  "/rigging-service.html": "/buy.html",
};

/**
 * 导航项的唯一权威清单（顺序即展示顺序）。
 *
 * 各页面 HTML 里的 `<nav class="nav">` 内容由本清单在运行时**规范化**：
 * 顺序按这里重排、缺失的补上、不在清单里的（例如已删除页面的残留）移除。
 * 这样即使某个页面手写的导航少了项，前台也不会出现不一致。
 * 文案仍由 i18n 按 href 翻译（`key` 是 data-i18n 兜底用的字典键）。
 */
const NAV_ITEMS: { href: string; key: string }[] = [
  { href: "/", key: "nav.home" },
  { href: "/products.html", key: "nav.products" },
  { href: "/quickstart.html", key: "nav.quickstart" },
  { href: "/plugin-manuals.html", key: "nav.manuals" },
  { href: "/advanced.html", key: "nav.advanced" },
  { href: "/buy.html", key: "nav.buy" },
  { href: "/support.html", key: "nav.support" },
  { href: "/update.html", key: "nav.update" },
  { href: "/lookup.html", key: "nav.lookup" },
];

const TEXT = {
  toc: { zh: "本页大纲", en: "On this page" },
  menu: { zh: "本页大纲", en: "On this page" },
  navMenu: { zh: "菜单", en: "Menu" },
  top: { zh: "回到顶部", en: "Back to top" },
  nav: { zh: "站点导航", en: "Site navigation" },
  brand: { zh: "MusicDoll 系列软件", en: "MusicDoll Series" },
};

/** 少于该数量的标题就不生成「本页大纲」，左栏也不出现 */
const MIN_HEADINGS = 3;

/** 左栏折叠为抽屉的断点（与 CSS 保持一致） */
const DRAWER_MAX = 639;

function lang(): "zh" | "en" {
  return document.documentElement.lang === "en" ? "en" : "zh";
}

function pick(key: keyof typeof TEXT): string {
  return TEXT[key][lang()];
}

function normalizePath(path: string): string {
  if (path.endsWith("/")) return `${path}index.html`;
  return path;
}

/** 当前页面应当高亮的导航 href */
function activeHref(): string {
  const path = normalizePath(location.pathname);
  const exact = EXACT_ACTIVE[path];
  if (exact) return exact;
  for (const g of FAMILY) {
    if (g.prefix && path.startsWith(g.prefix)) return g.href;
  }
  return path;
}

/**
 * 测量导航条高度并写入 --nav-h。
 *
 * 重要：CSS 里任何**决定导航条自身高度**的属性都不能引用 --nav-h，
 * 否则会形成「写变量 → 变高 → ResizeObserver 再测量 → 再写变量」的
 * 自增长死循环（表现为每帧长 1px）。目前 .site-navbar-inner 的 min-height
 * 是静态值。
 */
function measureNavbar(navbar: HTMLElement): void {
  let last = -1;
  let pending = false;

  const apply = () => {
    pending = false;
    // 向上取整：吸顶偏移宁可多 1px，也不能让内容被遮住
    const h = Math.ceil(navbar.getBoundingClientRect().height);
    if (h > 0 && h !== last) {
      last = h;
      document.documentElement.style.setProperty("--nav-h", `${h}px`);
    }
  };

  const schedule = () => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(apply);
  };

  apply();
  window.addEventListener("resize", schedule, { passive: true });
  if (typeof ResizeObserver !== "undefined") {
    new ResizeObserver(schedule).observe(navbar);
  }
}

function makeButton(className: string, html: string): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.className = className;
  b.innerHTML = html;
  return b;
}

function markActive(nav: HTMLElement): void {
  const current = activeHref();
  nav.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
    const href = normalizePath(
      new URL(a.getAttribute("href") || "/", location.origin).pathname,
    );
    a.classList.toggle("active", href === current);
  });
}

/**
 * 把 `.nav` 的内容规范化为 NAV_ITEMS：按清单顺序重排、补齐缺失项、
 * 移除不在清单里的链接（例如已删除页面的残留）。
 *
 * 优先复用页面里已有的 `<a>`（保留其文案，i18n 也会按 href 翻译）；
 * 只有页面确实没有该项时才新建，新建项带 data-i18n 交由 i18n 填字。
 */
function normalizeNav(nav: HTMLElement): void {
  const pathOf = (a: HTMLAnchorElement) =>
    normalizePath(
      new URL(a.getAttribute("href") || "/", location.origin).pathname,
    );

  const byHref = new Map<string, HTMLAnchorElement>();
  nav.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
    const p = pathOf(a);
    if (!byHref.has(p)) byHref.set(p, a);
  });

  for (const item of NAV_ITEMS) {
    const want = normalizePath(item.href);
    let a = byHref.get(want);
    if (!a) {
      a = document.createElement("a");
      a.href = item.href;
      a.setAttribute("data-i18n", item.key);
    }
    // appendChild 对已在文档中的元素等价于移动，因此这一步同时完成排序
    nav.appendChild(a);
  }

  const allowed = new Set(NAV_ITEMS.map((i) => normalizePath(i.href)));
  nav.querySelectorAll<HTMLAnchorElement>("a").forEach((a) => {
    if (!allowed.has(pathOf(a))) a.remove();
  });
}

/**
 * 保证站点导航条存在且结构完整：导航条 / 内容容器 / 品牌 / `.nav`。
 * 页面 HTML 正常已写好这四个部分；这里只做兜底与「把品牌搬到导航条左侧」。
 */
function ensureNavbar(body: HTMLElement): {
  navbar: HTMLElement;
  inner: HTMLElement;
  nav: HTMLElement;
} | null {
  const main = document.querySelector<HTMLElement>("main");
  if (!main) return null;

  let navbar = document.querySelector<HTMLElement>(".site-navbar");
  if (!navbar) {
    navbar = document.createElement("header");
    navbar.className = "site-navbar";
    const legacy = document.querySelector<HTMLElement>(".topbar");
    if (legacy) legacy.insertAdjacentElement("afterend", navbar);
    else body.insertBefore(navbar, main);
  }
  navbar.setAttribute("data-site-navbar", "");
  navbar.setAttribute("aria-label", pick("nav"));

  let inner = navbar.querySelector<HTMLElement>(".site-navbar-inner");
  if (!inner) {
    inner = document.createElement("div");
    inner.className = "site-navbar-inner";
    navbar.appendChild(inner);
  }

  // 品牌：优先复用页面里已有的 .brand（例如旧顶栏里的），否则新建
  let brand = document.querySelector<HTMLElement>(".brand");
  if (!brand) {
    brand = document.createElement("a");
    brand.className = "brand";
    brand.setAttribute("href", "/");
    brand.textContent = pick("brand");
  }
  if (!inner.contains(brand)) inner.prepend(brand);

  // 导航：优先复用页面里已有的 .nav，否则新建
  let nav = document.querySelector<HTMLElement>(".nav");
  if (!nav) {
    nav = document.createElement("nav");
    nav.className = "nav";
    nav.setAttribute("aria-label", "主导航");
  }
  if (!inner.contains(nav)) inner.appendChild(nav);

  return { navbar, inner, nav };
}

/**
 * 为没有自带面板的页面生成与教程页同构的「本页大纲」面板。
 * 教程页的 HTML 里已经带了这个面板（由 gen-tutorial-pages.mjs 生成）。
 */
function createTocPanel(): HTMLElement {
  const panel = document.createElement("aside");
  panel.className = "toc-panel";
  panel.setAttribute("data-toc-panel", "");
  panel.setAttribute("aria-label", pick("toc"));
  panel.innerHTML = `
    <div class="toc-progress" aria-hidden="true"><span data-toc-progress></span></div>
    <div class="toc-head"><span id="toc-head-title">${pick("toc")}</span></div>
    <nav class="toc-scroll" id="toc-scroll"><div id="toc-mount" data-toc-mount></div></nav>
    <button class="toc-top" type="button" data-toc-top>${pick("top")}</button>
  `;
  return panel;
}

function buildShell(): void {
  const body = document.body;
  // 管理后台等页面不接入站点外壳
  if (body.hasAttribute("data-no-rail")) return;
  if (body.querySelector("[data-rail]")) return;

  const shell = ensureNavbar(body);
  if (!shell) return;
  const { navbar, inner, nav } = shell;

  const main = document.querySelector<HTMLElement>("main");
  const tocPanelExists =
    document.querySelector<HTMLElement>("[data-toc-panel]");
  let tocRequested = !!tocPanelExists;

  /* ---------- 1. 导航项规范化（全站统一） ---------- */

  normalizeNav(nav);

  /* ---------- 1b. 窄屏汉堡菜单 ---------- */

  const navToggle = makeButton(
    "nav-toggle",
    `<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="M1.5 3.5h13M1.5 8h13M1.5 12.5h13" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg><span class="nav-toggle-text" data-nav-toggle-text>${pick("navMenu")}</span>`,
  );
  navToggle.setAttribute("data-nav-toggle", "");
  navToggle.setAttribute("aria-controls", "site-nav");
  navToggle.setAttribute("aria-expanded", "false");
  if (!nav.id) nav.id = "site-nav";
  inner.appendChild(navToggle);

  const setNavOpen = (open: boolean) => {
    body.classList.toggle("nav-open", open);
    navToggle.setAttribute("aria-expanded", open ? "true" : "false");
  };

  navToggle.addEventListener("click", (ev) => {
    ev.stopPropagation();
    setNavOpen(!body.classList.contains("nav-open"));
  });

  nav.addEventListener("click", (ev) => {
    if ((ev.target as HTMLElement).closest("a")) setNavOpen(false);
  });

  document.addEventListener("click", (ev) => {
    if (!body.classList.contains("nav-open")) return;
    const target = ev.target as HTMLElement;
    if (navbar.contains(target)) return;
    setNavOpen(false);
  });

  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && body.classList.contains("nav-open")) {
      setNavOpen(false);
    }
  });

  window.addEventListener("resize", () => {
    if (window.innerWidth > 1199) setNavOpen(false);
  });

  /* ---------- 2. 左侧「本页大纲」列（按需创建） ---------- */

  let rail: HTMLElement | null = null;
  let toggle: HTMLButtonElement | null = null;
  let drawerBound = false;

  const ensureRail = (): HTMLElement | null => {
    if (rail) return rail;
    if (!main) return null;

    rail = document.createElement("aside");
    rail.className = "site-rail";
    rail.id = "site-rail";
    rail.setAttribute("data-rail", "");
    rail.setAttribute("aria-label", pick("toc"));

    navbar.insertAdjacentElement("afterend", rail);
    body.classList.add("has-rail");

    // 大纲按钮（窄屏打开左栏抽屉）。图标刻意与汉堡菜单不同：
    // 手机宽度下两个按钮会并排出现，同款图标会让人以为是同一个功能。
    toggle = makeButton(
      "rail-toggle",
      `<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><circle cx="3" cy="4" r="1.1" fill="currentColor"/><circle cx="3" cy="8" r="1.1" fill="currentColor"/><circle cx="3" cy="12" r="1.1" fill="currentColor"/><path d="M6.5 4h7M6.5 8h7M6.5 12h7" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/></svg><span class="rail-toggle-text" data-rail-toggle-text>${pick("menu")}</span>`,
    );
    toggle.setAttribute("data-rail-toggle", "");
    toggle.setAttribute("aria-controls", "site-rail");
    toggle.setAttribute("aria-expanded", "false");
    inner.appendChild(toggle);

    if (!drawerBound) {
      drawerBound = true;
      bindDrawer(body, rail, toggle);
    }
    return rail;
  };

  /** 把大纲面板挂进左栏 */
  const mountToc = (panel: HTMLElement): void => {
    const host = ensureRail();
    if (!host) return;
    const tocHeading = panel.querySelector<HTMLElement>("#toc-head-title");
    if (tocHeading) tocHeading.textContent = pick("toc");
    const block = document.createElement("div");
    block.className = "rail-block rail-block-toc";
    block.setAttribute("data-rail-toc", "");
    block.appendChild(panel);
    host.insertBefore(block, host.firstChild);
  };

  /**
   * 确保「本页大纲」存在：教程页自带面板；其他页在标题足够多时现场创建，
   * 并把 main 标记为大纲根节点（buildToc 依赖 [data-toc-root]）。
   * 标题异步渲染的页面会在后续 refresh() 里补上。
   */
  const ensureToc = (): boolean => {
    if (tocRequested) return true;
    // 落地页 / 枢纽页用 <body data-no-toc> 显式关掉自动大纲：
    // 否则卡片里的 h3 会被当成"章节"塞进左栏，看着像是把页面标题又抄了一遍。
    if (body.hasAttribute("data-no-toc")) return false;
    if (!main || main.querySelectorAll("h2, h3").length < MIN_HEADINGS)
      return false;
    const panel = createTocPanel();
    if (!document.querySelector("[data-toc-root]")) {
      main.setAttribute("data-toc-root", "");
    }
    mountToc(panel);
    tocRequested = true;
    return true;
  };

  if (tocPanelExists) mountToc(tocPanelExists);

  /* ---------- 3. 头部高度与高亮 ---------- */

  markActive(nav);
  measureNavbar(navbar);

  /* ---------- 4. 标题锚点 + 大纲刷新 ---------- */

  if (main) {
    let scheduled = false;
    let lastHeads = "";
    const headingKey = () =>
      Array.from(main.querySelectorAll<HTMLElement>("h2, h3"))
        .map((h) => h.id || headingLabel(h))
        .join("|");

    const refresh = () => {
      scheduled = false;
      decorateHeadings(main);
      if (!ensureToc()) return;
      const key = headingKey();
      if (key === lastHeads) return;
      lastHeads = key;
      buildToc();
    };
    const schedule = () => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(refresh);
    };

    decorateHeadings(main);
    refresh();
    if (typeof MutationObserver !== "undefined") {
      new MutationObserver(schedule).observe(main, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }
    window.addEventListener("load", refresh, { once: true });
  }

  /* ---------- 5. 语言切换后刷新本模块写的文案 ---------- */

  document.addEventListener("langchange", () => {
    const tocHeading = document.querySelector<HTMLElement>("#toc-head-title");
    if (tocHeading) tocHeading.textContent = pick("toc");
    const toggleText = toggle?.querySelector<HTMLElement>(
      "[data-rail-toggle-text]",
    );
    if (toggleText) toggleText.textContent = pick("menu");
    const navToggleText = navToggle.querySelector<HTMLElement>(
      "[data-nav-toggle-text]",
    );
    if (navToggleText) navToggleText.textContent = pick("navMenu");
    const top = document.querySelector<HTMLElement>("[data-toc-top]");
    if (top) top.textContent = pick("top");
    navbar.setAttribute("aria-label", pick("nav"));
    rail?.setAttribute("aria-label", pick("toc"));
    markActive(nav);
  });
}

function bindDrawer(
  body: HTMLElement,
  rail: HTMLElement,
  toggle: HTMLButtonElement,
): void {
  const backdrop = document.createElement("div");
  backdrop.className = "rail-backdrop";
  backdrop.setAttribute("data-rail-backdrop", "");
  body.appendChild(backdrop);

  const setOpen = (open: boolean) => {
    body.classList.toggle("rail-open", open);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    if (open) {
      rail
        .querySelector<HTMLElement>("a, button")
        ?.focus({ preventScroll: true });
    } else if (document.activeElement && rail.contains(document.activeElement)) {
      toggle.focus({ preventScroll: true });
    }
  };

  toggle.addEventListener("click", () => {
    setOpen(!body.classList.contains("rail-open"));
  });
  backdrop.addEventListener("click", () => setOpen(false));
  document.addEventListener("keydown", (ev) => {
    if (ev.key === "Escape" && body.classList.contains("rail-open")) {
      ev.preventDefault();
      setOpen(false);
    }
  });
  window.addEventListener("resize", () => {
    if (window.innerWidth > DRAWER_MAX) setOpen(false);
  });
}

buildShell();
